// composables/useMigration.ts
import { openDB } from 'idb'

const DB_NAME = 'migration-db'
const DB_VERSION = 1
const POLICIES_STORE = 'policies'
const PENDING_POLICIES_STORE = 'pending-policies'

let dbPromise: ReturnType<typeof openDB>

export type DbItemType = {
	indexNumber: number
	record: {
		id: string
		name: string
		dependant_count: number
	}
}

const initDB = async () => {
	if (!dbPromise) {
		dbPromise = openDB(DB_NAME, DB_VERSION, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(POLICIES_STORE)) {
					db.createObjectStore(POLICIES_STORE)
				}
				if (!db.objectStoreNames.contains(PENDING_POLICIES_STORE)) {
					db.createObjectStore(PENDING_POLICIES_STORE)
				}
			}
		})
	}
	return dbPromise
}

export const useMigration = () => {
	const hasData = shallowRef(false)
	const totalRecords = shallowRef(0)

	const updateHasData = async (key = 'all') => {
		const db = await initDB()
		const tx = db.transaction([POLICIES_STORE, PENDING_POLICIES_STORE], 'readonly')
		const store1 = tx.objectStore(POLICIES_STORE)
		const store2 = tx.objectStore(PENDING_POLICIES_STORE)
		const [store2Data, store1Data] = await Promise.all([store2.get(key), store1.get(key)])
		totalRecords.value = Number(store2Data?.length ?? 0) + Number(store1Data?.length ?? 0)
		hasData.value = (store2Data || store1Data) && (store2Data?.length > 0 || store1Data?.length > 0)
	}

	const hasPolicyToMigrate = computed(() => hasData.value)

	const addItems = async <T = DbItemType[]>(storeName: 'policies' | 'pending-policies', items: T[], key = 'all') => {
		const db = await initDB()
		const tx = db.transaction(storeName, 'readwrite')
		const store = tx.objectStore(storeName)
		await store.put(items, key)
		await tx.done
		await updateHasData(key)
	}

	const clearStore = async (storeName: 'policies' | 'pending-policies', key = 'all') => {
		const db = await initDB()
		const tx = db.transaction(storeName, 'readwrite')
		await tx.objectStore(storeName).clear()
		await tx.done
		await updateHasData(key)
	}

	const getItems = async <T = DbItemType[] | []>(storeName: 'policies' | 'pending-policies', key = 'all') => {
		const db = await initDB()
		const tx = db.transaction(storeName, 'readonly')
		const items = await tx.objectStore(storeName).get(key)
		await tx.done
		return items as T
	}
	const findByStatus = async (arg: 'active' | 'pending', fn: (res: DbItemType[]) => void, key = 'all') => {
		let result: DbItemType[] | [] = []
		await updateHasData(key)
		if (arg === 'active') {
			result = await getItems('policies', key)
		} else {
			result = await getItems('pending-policies', key)
		}
		if (result && result.length > 0) {
			fn(result)
		}
	}
	const formatItems = (list: EmployeesResponse[], indexList: (string | number)[]) => {
		return list.map((item, i) => ({
			id: item.id,
			indexNumber: Number(indexList[i]),
			record: {
				id: item.id,
				name: item.first_name + ' ' + item.last_name,
				dependant_count: item.meta?.no_of_dependant ?? 0
			}
		})) as DbItemType[]
	}

	return {
		hasPolicyToMigrate,
		addItems,
		clearStore,
		getItems,
		updateHasData,
		findByStatus,
		totalRecords,
		formatItems
	}
}
